import { LegalPageLayout } from "./legal/LegalPageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, Users, Shield, Lightbulb, Sparkles, Target, BookOpen, TrendingUp, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";

const Mission = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="Notre Mission — Transformer l'Accès aux Soins en Addictologie"
        description="Découvrez la vision de Libr'Addict : rendre l'accompagnement en addictologie accessible, éthique et profondément humain."
      />
      <LegalPageLayout title="Notre Mission">
        <div className="space-y-8 animate-fade-in">
          {/* Hero Card */}
          <Card className="border border-primary/20 bg-card shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
                <Badge variant="accent" className="text-sm">Le Début d'une Révolution</Badge>
              </div>
              <CardTitle className="text-3xl md:text-4xl bg-gradient-primary bg-clip-text text-transparent">
                Ceci n'est que le début.
              </CardTitle>
              <CardDescription className="text-lg mt-4 text-foreground/80">
                Le vrai travail commence maintenant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-lg leading-relaxed">
                Construire <span className="font-bold text-primary">Libr'Addict</span>, une plateforme pensée pour rendre 
                l'accompagnement en addictologie <span className="font-semibold">accessible</span>, 
                <span className="font-semibold"> éthique</span> et <span className="font-semibold">profondément humain</span>.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                À partir de maintenant, tout devient concret : la communication, la structuration du réseau, 
                les financements, les partenariats, la recherche de professionnel·les qualifié·es.
              </p>
            </CardContent>
          </Card>

          {/* Challenge Card */}
          <Card className="glass-card hover-scale">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-7 h-7 text-accent" />
                <CardTitle className="text-2xl">Un Besoin Criant, Une Réponse Insuffisante</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="leading-relaxed">
                <span className="font-semibold text-accent">Le défi est immense</span> : rassembler de véritables 
                spécialistes de l'addiction, des thérapeutes et des pair·es aidant·es, car les besoins explosent.
              </p>
              <div className="bg-destructive/10 border-l-4 border-destructive p-4 rounded-lg">
                <p className="leading-relaxed text-foreground/90">
                  <strong>Aujourd'hui</strong>, la majorité des personnes concernées ne trouvent pas d'aide rapidement 
                  — ou se heurtent au <span className="font-semibold">jugement</span>, au <span className="font-semibold">coût</span>, 
                  ou à <span className="font-semibold">l'absence totale de suivi</span>.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Ambition Card */}
          <Card className="border border-accent/30 bg-card shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Lightbulb className="w-7 h-7 text-primary" />
                <CardTitle className="text-2xl">Notre Ambition</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-lg font-semibold leading-relaxed text-primary">
                Mon objectif est simple mais ambitieux :
              </p>
              <div className="bg-primary/10 border-l-4 border-primary p-5 rounded-lg">
                <p className="text-lg leading-relaxed font-medium">
                  Faire de l'accès à l'aide en addictologie quelque chose d'aussi simple que de prendre un rendez-vous médical, 
                  tout en plaçant la <span className="text-primary font-bold">prévention</span>, 
                  la <span className="text-primary font-bold">réduction des risques</span> et 
                  la <span className="text-primary font-bold">dignité</span> au cœur du processus.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Formation Card */}
          <Card className="glass-card hover-scale">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="w-7 h-7 text-accent" />
                <CardTitle className="text-2xl">Former la Prochaine Génération d'Experts</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="leading-relaxed">
                Libr'Addict proposera aussi un <span className="font-semibold text-accent">espace dédié à la formation</span> de 
                nouveaux expert·es en addiction et pair·es aidant·es.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                Car la voie médicale traditionnelle est longue et sélective, alors que nous avons besoin de 
                <span className="font-semibold text-foreground"> centaines — voire de milliers</span> — de professionnel·les 
                supplémentaires <span className="font-semibold text-foreground">dès maintenant</span>.
              </p>
            </CardContent>
          </Card>

          {/* Movement Card */}
          <Card className="border border-primary/30 bg-background">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Heart className="w-8 h-8 text-primary fill-primary" />
                <CardTitle className="text-2xl md:text-3xl">Plus Qu'une Application, Un Mouvement</CardTitle>
              </div>
              <CardDescription className="text-lg font-semibold text-foreground/80 mt-2">
                Une nouvelle manière de penser le soin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-lg leading-relaxed font-medium">
                <span className="text-primary font-bold">Libr'Addict</span> n'est pas qu'une application.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex flex-col items-center text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <Users className="w-10 h-10 text-primary mb-3" />
                  <h4 className="font-semibold mb-2">Recréer la Confiance</h4>
                  <p className="text-sm text-muted-foreground">Entre les personnes en demande d'aide et celles qui peuvent l'apporter</p>
                </div>
                <div className="flex flex-col items-center text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <TrendingUp className="w-10 h-10 text-accent mb-3" />
                  <h4 className="font-semibold mb-2">Unifier les Ressources</h4>
                  <p className="text-sm text-muted-foreground">Autour d'une plateforme claire et bienveillante</p>
                </div>
                <div className="flex flex-col items-center text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <Shield className="w-10 h-10 text-primary mb-3" />
                  <h4 className="font-semibold mb-2">Traiter avec Dignité</h4>
                  <p className="text-sm text-muted-foreground">L'addiction avec l'empathie, la compétence et le respect qu'elle mérite</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Card */}
          <Card className="border border-accent/40 bg-background shadow-sm">
            <CardContent className="pt-8 space-y-6">
              <div className="text-center space-y-4">
                <p className="text-xl font-bold text-foreground">Oui, c'est ambitieux.</p>
                <div className="bg-gradient-primary bg-clip-text text-transparent">
                  <p className="text-xl md:text-2xl font-bold leading-relaxed max-w-3xl mx-auto">
                    Nous pouvons transformer la façon dont on accède à l'aide, au soutien, 
                    et au soin en addictologie.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
                <Button size="lg" className="shadow-sm" onClick={() => navigate("/resources")}>
                  <BookOpen className="w-5 h-5 mr-2" />
                  Découvrir les ressources
                </Button>
                <Button size="lg" variant="destructive" onClick={() => navigate("/panic")}>
                  <Phone className="w-5 h-5 mr-2" />
                  Numéros d'urgence
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default Mission;
